import { MMButton } from '@components'
import { CloseIcon } from '@icons'
import cn from '@integrations/classnames'
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from '@integrations/react'
import { useNavigate } from '@integrations/routing'
import { useDispatch } from '@integrations/state'
import {
  api,
  clearIbanDraft,
  deriveIbanFeeState,
  extractIssuedIban,
  hasIbanQuestionnaireFieldError,
  IBAN_QUESTIONNAIRE_FIELDS,
  loadIbanDraft,
  mapIssueStatus,
  paths,
  responseDoesNotContainsChallenge,
  saveIbanDraft,
  selectCurrentUserRole,
  selectMerchantCapabilities,
  useAppSelector,
  useIbanRequestInitMutation,
  useIbanRequestPreviewMutation,
  useIbanRequestQry,
  useMerchantAccounts,
  useMerchantQry
} from 'mmfintech-backend-api'
import type {
  AccountBalanceResponse,
  BankAccountIssuingPreviewRequest,
  BankAccountIssuingRequest,
  BankAccountQuestionnaireRequest,
  BankAccountScheme,
  Currency,
  IbanIssueStatus,
  PaymentResponse
} from 'mmfintech-backend-api/types'
import { GlobalContext, isValidArray, OtpContext, tr } from 'mmfintech-commons'

import ConfirmStep from './ConfirmStep'
import { IbanStep, type IbanStepValue, isIbanIssuingEligible } from './helpers'
import IbanFeeTermsModal from './IbanFeeTermsModal'
import styles from './IbanRequest.module.scss'
import QuestionnaireStep from './QuestionnaireStep'
import SetupStep from './SetupStep'
import StatusStep from './StatusStep'

interface Props {
  onClose: () => void
  reload: () => void
  selectedAccountId?: number
}

interface StatusResult {
  status: IbanIssueStatus
  iban?: string
  response?: PaymentResponse
}

interface StepDescriptor {
  step: IbanStepValue
  label: string
}

function IbanRequestFlow({ onClose, reload, selectedAccountId }: Props) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { modalShow } = useContext(GlobalContext)
  const { setOtpOnSuccess } = useContext(OtpContext)

  const customerRole = useAppSelector(selectCurrentUserRole)
  const capabilities = useAppSelector(selectMerchantCapabilities)
  const { merchant, isMerchantIndividual, merchantLoaded } = useMerchantQry()

  const [step, setStep] = useState<IbanStepValue>(IbanStep.SETUP)
  const [maxStepReached, setMaxStepReached] = useState<IbanStepValue>(IbanStep.SETUP)
  const [statusResult, setStatusResult] = useState<StatusResult | null>(null)
  const [countryListError, setCountryListError] = useState('')
  const [pendingAccountId, setPendingAccountId] = useState<number | null>(null)

  const restoredRef = useRef({ scheme: false, currency: false, account: false, form: false })

  // Restore only a draft that belongs to the current company. `loadIbanDraft` enforces the
  // same-merchant rule internally, so a stale fee/state can't bleed across companies.
  const draft = useMemo(() => loadIbanDraft(merchant?.merchantId), [merchant?.merchantId])

  const {
    selectedScheme,
    setSelectedScheme,
    selectedCurrency,
    setSelectedCurrency,
    selectedAccount,
    setSelectedAccount,
    availableSchemes,
    ibanSchemeCurrencies,
    ibanSchemeCurrenciesFetching,
    ibanIssuingFee,
    fees,
    feesFetching,
    accounts,
    accountsFetching,
    resetMutations,
    countryList,
    setCountryList,
    formValues
  } = useIbanRequestQry({
    hasQuestionnaire: isMerchantIndividual,
    shouldSkipIssuingFee: true,
    // We drive preview/initiate ourselves so the fee is debited from the chosen "Pay from" account.
    onFee: () => {}
  })

  const [triggerPreview, { error: previewError, isLoading: previewFetching }] = useIbanRequestPreviewMutation({
    fixedCacheKey: 'iban-request-preview'
  })
  const [triggerInitiate, { data: initData, error: initiateError, isLoading: initiateFetching }] =
    useIbanRequestInitMutation({
      fixedCacheKey: 'iban-request-initiate'
    })

  // Latest terminal init response, read from the OTP-success callback (which fires after the
  // wizard's own submit promise has already resolved with the challenge response).
  const initDataRef = useRef<PaymentResponse | undefined>(undefined)
  useEffect(() => {
    initDataRef.current = initData as PaymentResponse | undefined
  }, [initData])

  // Only accounts that can receive a new IBAN are linkable (exclude ones already holding an IBAN).
  const linkableAccounts = useMemo(
    () =>
      accounts.filter(
        (account) => !account.dedicatedMerchantBankAccounts?.some((dedicated: { iban?: string }) => dedicated?.iban)
      ),
    [accounts]
  )

  // Fee tri-state, derived once via the shared selector so Setup and Confirm read one source of truth.
  const feeState = useMemo(
    () => deriveIbanFeeState(fees, ibanIssuingFee, { feesFetching, selectedCurrency }),
    [fees, ibanIssuingFee, feesFetching, selectedCurrency]
  )
  const { feePaid, feeValue, feeCurrency, showFee } = feeState

  // "Pay from" funding account (independent of the linked IBAN account).
  const { activeAccounts: payFromAccounts } = useMerchantAccounts({
    validCurrency: (currency: Currency) => currency.currencyCode === feeCurrency
  })
  const [payFromAccount, setPayFromAccount] = useState<AccountBalanceResponse | null>(null)

  // ── Stepper (Status shown as the terminal "Done" node) ──
  const visibleSteps = useMemo<StepDescriptor[]>(() => {
    const list: StepDescriptor[] = [{ step: IbanStep.SETUP, label: tr('FRONTEND.IBAN_REQUEST.STEP_SETUP', 'Setup') }]
    if (isMerchantIndividual) {
      list.push({
        step: IbanStep.QUESTIONNAIRE,
        label: tr('FRONTEND.IBAN_REQUEST.STEP_QUESTIONNAIRE', 'Questionnaire')
      })
    }
    list.push({ step: IbanStep.CONFIRM, label: tr('FRONTEND.IBAN_REQUEST.STEP_CONFIRM', 'Confirm & pay') })
    list.push({ step: IbanStep.STATUS, label: tr('FRONTEND.IBAN_REQUEST.STEP_DONE', 'Done') })
    return list
  }, [isMerchantIndividual])

  function goToStep(next: IbanStepValue) {
    setStep(next)
    setMaxStepReached((prev) => (next > prev ? next : prev))
  }

  function goForward() {
    const idx = visibleSteps.findIndex((s) => s.step === step)
    if (idx >= 0 && idx < visibleSteps.length - 1) goToStep(visibleSteps[idx + 1].step)
  }

  function goBackStep() {
    const idx = visibleSteps.findIndex((s) => s.step === step)
    if (idx > 0) setStep(visibleSteps[idx - 1].step)
    else onClose()
  }

  function handleSetupContinue() {
    if (selectedScheme && selectedCurrency && selectedAccount) goForward()
  }

  function handleQuestionnaireContinue() {
    if (!isValidArray(countryList)) {
      setCountryListError(tr('VALIDATION.ERRORS.REQUIRED', 'This field should not be empty'))
    }
    if (!formValues.areValid() || !isValidArray(countryList)) return
    goForward()
  }

  function showStatus(response?: PaymentResponse) {
    const status = mapIssueStatus(response)
    if (status !== 'failed') {
      clearIbanDraft()
      dispatch(api.util.invalidateTags(['Accounts']))
      reload()
    }
    setStatusResult({ status, iban: extractIssuedIban(response), response })
    setStep(IbanStep.STATUS)
  }

  // A terminal (non-challenge) initiate response from the wizard's own submit promise.
  function finalize(response?: PaymentResponse) {
    if (!response || !responseDoesNotContainsChallenge(response)) return
    showStatus(response)
  }

  // Runs after the global OTP modal verifies. The wizard stays mounted underneath, so we read the
  // now-terminal init response from the shared cache and render the Status step in place.
  function finalizeAfterOtp() {
    showStatus(initDataRef.current)
  }

  function handleSubmit() {
    if (!selectedScheme || !selectedCurrency || !selectedAccount) return
    const fundingAccountId = payFromAccount?.id ?? selectedAccount.id
    const previewPayload: BankAccountIssuingPreviewRequest = {
      scheme: selectedScheme,
      currency: selectedCurrency,
      issuingAccountId: selectedAccount.id
    }
    const questionnaire: BankAccountQuestionnaireRequest | undefined = isMerchantIndividual
      ? {
          countryList: countryList ?? [],
          yearlyTransactionsAmountInEur: String(formValues.getValue('yearlyTransactionsAmountInEur') ?? ''),
          totalNumOfBankAccounts: Number(formValues.getValue('totalNumOfBankAccounts') ?? 0),
          linkedBankAccountsCount: Number(formValues.getValue('linkedBankAccountsCount') ?? 0),
          occupation: String(formValues.getValue('occupation') ?? ''),
          purposeOfAccount: String(formValues.getValue('purposeOfAccount') ?? '')
        }
      : undefined
    const initiatePayload: BankAccountIssuingRequest = {
      ...previewPayload,
      paymentAccountId: fundingAccountId,
      ...(questionnaire ? { bankAccountQuestionnaireRequest: questionnaire } : {})
    }
    // Wire OTP so a challenged initiate still lands on the Status step once confirmed.
    setOtpOnSuccess?.(() => finalizeAfterOtp)
    triggerPreview(previewPayload)
      .unwrap()
      .then(() => triggerInitiate(initiatePayload).unwrap())
      .then((response: PaymentResponse) => finalize(response))
      .catch((err: unknown) => {
        // Questionnaire validation failure → jump back and surface field errors.
        if (hasIbanQuestionnaireFieldError(err)) {
          formValues.handleErrors(err)
          setStep(IbanStep.QUESTIONNAIRE)
        }
        // Other rejections surface inline via previewError / initiateError.
      })
  }

  function persistDraft() {
    if (step === IbanStep.STATUS) return
    const questionnaire: Record<string, string> = {}
    IBAN_QUESTIONNAIRE_FIELDS.forEach((field) => {
      const value = formValues.getValue(field)
      if (value != null) questionnaire[field] = String(value)
    })
    saveIbanDraft(merchant?.merchantId, {
      scheme: selectedScheme ?? undefined,
      currency: selectedCurrency ?? undefined,
      accountId: selectedAccount?.id,
      step,
      maxStepReached,
      countryList: countryList ?? undefined,
      questionnaire
    })
  }

  function handleTopUp() {
    persistDraft()
    const target = payFromAccount?.id ?? selectedAccount?.id
    onClose()
    // Banky's DepositView reads the preselected account from router state key `preSelectedAccount`.
    navigate(paths.banking.deposit(), { state: target ? { preSelectedAccount: target } : undefined })
  }

  function openFeeTerms() {
    modalShow({
      options: { size: 'medium' },
      content: <IbanFeeTermsModal />
    })
  }

  function resetFlow() {
    clearIbanDraft()
    resetMutations()
    setStatusResult(null)
    setSelectedAccount(null)
    setPayFromAccount(null)
    dispatch(api.util.invalidateTags(['Accounts']))
    reload()
    // The flow restarts in place (no remount), so force a fee refetch for the just-issued
    // currency+scheme (fees are cached per currency+scheme).
    if (selectedCurrency && selectedScheme) {
      dispatch(
        (
          api.endpoints as { getBankAccountFees: { initiate: (arg: unknown, opts: unknown) => unknown } }
        ).getBankAccountFees.initiate(
          { currency: selectedCurrency, bankAccountScheme: selectedScheme },
          { forceRefetch: true, subscribe: false }
        ) as never
      )
    }
    setStep(IbanStep.SETUP)
    setMaxStepReached(IbanStep.SETUP)
  }

  function goToAccounts() {
    clearIbanDraft()
    reload()
    onClose()
    navigate(paths.banking.accounts.list())
  }

  // Default the funding account when reaching Confirm: prefer one that can cover the fee, else the
  // linked account, else the first — without overriding a user's own choice.
  useEffect(() => {
    if (step !== IbanStep.CONFIRM || payFromAccount || !isValidArray(payFromAccounts)) return
    const funded = payFromAccounts.find((a) => Number(a.balance ?? 0) >= feeValue)
    const linked = payFromAccounts.find((a) => a.id === selectedAccount?.id)
    setPayFromAccount(funded ?? linked ?? payFromAccounts[0])
  }, [step, payFromAccounts, payFromAccount, feeValue, selectedAccount?.id])

  // Auto-select a freshly created account once it appears in the list.
  useEffect(() => {
    if (pendingAccountId && isValidArray(linkableAccounts)) {
      const match = linkableAccounts.find((a) => a.id === pendingAccountId)
      if (match) {
        setSelectedAccount(match)
        setPendingAccountId(null)
      }
    }
  }, [linkableAccounts, pendingAccountId, setSelectedAccount])

  // Preselect the account passed in via props (entry-point contract).
  useEffect(() => {
    if (!selectedAccountId || accountsFetching || selectedAccount) return
    const match = linkableAccounts.find((a) => a.id === selectedAccountId)
    if (match) setSelectedAccount(match)
  }, [selectedAccountId, accountsFetching, linkableAccounts, selectedAccount, setSelectedAccount])

  // ── Persist draft on meaningful changes (survive a top-up round-trip / refresh) ──
  // biome-ignore lint/correctness/useExhaustiveDependencies: snapshot on selection/step changes
  useEffect(() => {
    persistDraft()
  }, [selectedScheme, selectedCurrency, selectedAccount?.id, step, maxStepReached, countryList])

  // ── Restore draft (best-effort) once data is available ──
  useEffect(() => {
    if (!draft) return
    if (!restoredRef.current.scheme && draft.scheme && isValidArray(availableSchemes)) {
      if (availableSchemes.includes(draft.scheme as BankAccountScheme))
        setSelectedScheme(draft.scheme as BankAccountScheme)
      restoredRef.current.scheme = true
    }
  }, [draft, availableSchemes, setSelectedScheme])

  useEffect(() => {
    if (!draft) return
    if (
      !restoredRef.current.currency &&
      restoredRef.current.scheme &&
      draft.currency &&
      draft.scheme &&
      selectedScheme === draft.scheme &&
      isValidArray(ibanSchemeCurrencies?.[selectedScheme])
    ) {
      if (ibanSchemeCurrencies[selectedScheme].includes(draft.currency)) setSelectedCurrency(draft.currency)
      restoredRef.current.currency = true
    }
  }, [draft, selectedScheme, ibanSchemeCurrencies, setSelectedCurrency])

  useEffect(() => {
    if (!draft) return
    if (!restoredRef.current.account && draft.accountId && isValidArray(linkableAccounts) && !accountsFetching) {
      const match = linkableAccounts.find((a) => a.id === draft.accountId)
      if (match) setSelectedAccount(match)
      restoredRef.current.account = true
    }
  }, [draft, linkableAccounts, accountsFetching, setSelectedAccount])

  // Restore questionnaire answers + step once (after merchant type is known).
  useEffect(() => {
    if (!draft || restoredRef.current.form || !merchantLoaded) return
    if (draft.questionnaire) {
      Object.entries(draft.questionnaire).forEach(([field, value]) => {
        formValues.setValue(field, value)
      })
    }
    if (isValidArray(draft.countryList)) setCountryList(draft.countryList)
    if (draft.step && draft.step !== IbanStep.STATUS) setStep(draft.step as IbanStepValue)
    if (draft.maxStepReached) setMaxStepReached(draft.maxStepReached as IbanStepValue)
    restoredRef.current.form = true
  }, [draft, merchantLoaded, formValues, setCountryList])

  if (!merchantLoaded) return null
  if (!isIbanIssuingEligible(customerRole, capabilities)) return null

  return (
    <div className={cn(styles['iban-flow-overlay'])} data-test='iban-request-flow'>
      <div className={cn(styles['iban-flow-modal'])}>
        <MMButton
          type='button'
          variant='secondary'
          collapsed
          collapsedIcon={<CloseIcon />}
          title={tr('FRONTEND.BUTTONS.CLOSE', 'Close')}
          onClick={onClose}
          className={cn(styles['iban-flow-close'])}
          data-test='iban-flow-close'
        />
        <div className={cn(styles['iban-flow-inner'])}>
          <div className={cn(styles['iban-flow-head'])}>
            <h1>{tr('FRONTEND.IBAN_REQUEST.TITLE', 'IBAN issuing request')}</h1>
          </div>

          <div className={cn(styles['iban-stepper'])}>
            {visibleSteps.map((entry, index) => {
              const state = step === entry.step ? 'active' : step > entry.step ? 'done' : ''
              return (
                <Fragment key={entry.step}>
                  {index > 0 && <span className={cn(styles['iban-step-arrow'])}>→</span>}
                  <div className={cn(styles['iban-step'], { [styles[state]]: Boolean(state) })}>
                    <span className={cn(styles['iban-step-marker'], 'iban-step-marker', styles['iban-step-num'])}>
                      {index + 1}
                    </span>
                    <span>{entry.label}</span>
                  </div>
                </Fragment>
              )
            })}
          </div>

          {step === IbanStep.SETUP && (
            <SetupStep
              availableSchemes={availableSchemes}
              selectedScheme={selectedScheme}
              setSelectedScheme={setSelectedScheme}
              ibanSchemeCurrencies={ibanSchemeCurrencies}
              ibanSchemeCurrenciesFetching={ibanSchemeCurrenciesFetching}
              selectedCurrency={selectedCurrency}
              setSelectedCurrency={setSelectedCurrency}
              accounts={linkableAccounts}
              accountsFetching={accountsFetching}
              selectedAccount={selectedAccount}
              setSelectedAccount={setSelectedAccount}
              ibanIssuingFee={ibanIssuingFee}
              fees={fees}
              feePaid={feePaid}
              showFee={showFee}
              onReloadAccounts={(accountId?: number) => {
                resetMutations()
                if (accountId) setPendingAccountId(accountId)
              }}
              onContinue={handleSetupContinue}
              onCancel={onClose}
            />
          )}

          {step === IbanStep.QUESTIONNAIRE && (
            <QuestionnaireStep
              formValues={formValues}
              countryList={countryList ?? []}
              setCountryList={setCountryList}
              countryListError={countryListError}
              setCountryListError={setCountryListError}
              onBack={goBackStep}
              onContinue={handleQuestionnaireContinue}
            />
          )}

          {step === IbanStep.CONFIRM && (
            <ConfirmStep
              selectedScheme={selectedScheme}
              selectedCurrency={selectedCurrency}
              selectedAccount={selectedAccount}
              payFromAccounts={payFromAccounts}
              payFromAccount={payFromAccount}
              setPayFromAccount={setPayFromAccount}
              feeState={feeState}
              fees={fees}
              submitting={previewFetching || initiateFetching}
              error={[previewError, initiateError]}
              onBack={goBackStep}
              onSubmit={handleSubmit}
              onTopUp={handleTopUp}
              onOpenFeeTerms={openFeeTerms}
            />
          )}

          {step === IbanStep.STATUS && statusResult && (
            <StatusStep
              status={statusResult.status}
              iban={statusResult.iban}
              response={statusResult.response}
              onViewAccounts={goToAccounts}
              onIssueAnother={resetFlow}
              onContactSupport={() => {
                onClose()
                navigate(paths.support.tickets())
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default IbanRequestFlow
